/**
 * navigation/AppShell
 * ──────────────────────────────────────────────────────────────────────────
 * The application chrome: header, sidebar, the content area, the ledger drawer
 * and the modal host.
 *
 * ONE APPLICATION, NOT THREE. The console selects which nav and which screens
 * render, but there is a single shell, a single store and a single set of
 * primitives. That is why a fix to the drawer or a change to a status tone
 * lands in all three consoles at once.
 *
 * BELOW 1024px the sidebar becomes an overlay drawer, opened from a button in
 * the header and closed by the scrim or by choosing a view. The design drew no
 * mobile layout; this is the smallest change that keeps the same navigation
 * model working when a 214px column would leave nothing for the content.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { Pressable, StyleSheet, View } from 'react-native';
import { neutral, scrim } from '../theme/colors';
import { useResponsive } from '../lib/responsive';
import LedgerDrawer from '../screens/AssignmentRegister/components/LedgerDrawer';
import ModalHost from '../screens/modals/ModalHost';
import { useConfig } from '../context/ConfigContext';
import { useSession } from '../context/SessionContext';
import { useShell } from '../context/ShellContext';
import AppHeader from './components/AppHeader';
import Sidebar from './components/Sidebar';
import { CONSOLE_TITLE, NAV } from './routes';
import { useNavCounts } from './useNavCounts';
import ViewRouter from './ViewRouter';

export function AppShell() {
  const { orgLabel } = useSession();
  const { console: role, gates, categories } = useConfig();
  const {
    view,
    setView,
    navCollapsed,
    toggleNav,
    navOpen,
    setNavOpen,
    drawerAssignmentId,
    closeDrawer,
    openModal,
  } = useShell();
  const { overlayNav } = useResponsive();

  const counts = useNavCounts(
    role,
    categories.some((category) => category.is_active),
  );

  const sidebar = (
    <Sidebar
      title={CONSOLE_TITLE[role]}
      items={NAV[role]}
      active={view}
      onSelect={setView}
      collapsed={navCollapsed}
      onToggleCollapse={overlayNav ? () => setNavOpen(false) : toggleNav}
      counts={counts}
      overlay={overlayNav}
    />
  );

  return (
    <View style={styles.app}>
      <AppHeader
        orgLabel={orgLabel}
        // Every balance on screen states its as-of time. The Overview's payload
        // carries the authoritative one; the header shows "now" as the moment
        // the shell last rendered, which is what the design's stamp means.
        asOf={new Date().toISOString()}
        // "Assign cash" is a Finance action, and it needs somewhere for the
        // money to be denominated — so it waits for the console gate.
        onAssignCash={
          role === 'finance' && gates.console ? () => openModal({ kind: 'assign' }) : undefined
        }
        onOpenNav={overlayNav ? () => setNavOpen(true) : undefined}
      />

      <View style={styles.body}>
        {/* Docked on a wide viewport; an overlay panel below the tablet edge. */}
        {!overlayNav && sidebar}

        <View style={styles.content}>
          <ViewRouter view={view} />
        </View>

        {overlayNav && navOpen && (
          <>
            <Pressable
              style={styles.navScrim}
              onPress={() => setNavOpen(false)}
              accessibilityLabel="Close navigation"
            />
            {sidebar}
          </>
        )}

        {/* The drawer is an overlay over the CONTENT area, not a third column —
            as a column it squeezed the register until its cells collided. */}
        {!!drawerAssignmentId && (
          <LedgerDrawer assignmentId={drawerAssignmentId} onClose={closeDrawer} />
        )}
      </View>

      <ModalHost />
    </View>
  );
}

const styles = StyleSheet.create({
  app: { flex: 1, backgroundColor: neutral.surface },
  body: { flex: 1, flexDirection: 'row', minHeight: 0 },
  content: { flex: 1, minWidth: 0, backgroundColor: neutral.surface },
  navScrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: scrim.drawer,
    zIndex: 20,
  },
});

export default AppShell;
